import React, { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Confetti } from '../ui/Confetti';
import { Trophy, Share2, RotateCcw, Home, TrendingUp, Zap, Target } from 'lucide-react';
import { PLAYERS } from '../../lib/utils';
interface MatchStats {
  moves: number;
  duration: number;
  connectedChips: number;
  averageMoveTime: number;
}
interface PostMatchScreenProps {
  winner: number | null;
  matchStats: MatchStats;
  onRematch: () => void;
  onHome: () => void;
}
function calculateXP(stats: MatchStats, isWin: boolean): number {
  let xp = 0;
  // Base XP
  xp += isWin ? 100 : 50;
  // Speed bonus (faster = more XP)
  const avgMoveTime = stats.duration / stats.moves;
  if (avgMoveTime < 10) xp += 50;else if (avgMoveTime < 15) xp += 30;else if (avgMoveTime < 20) xp += 10;
  // Efficiency bonus (fewer moves = more XP)
  if (stats.moves < 10) xp += 40;else if (stats.moves < 15) xp += 20;
  // Connected chips bonus
  xp += stats.connectedChips * 5;
  return xp;
}
export function PostMatchScreen({
  winner,
  matchStats,
  onRematch,
  onHome
}: PostMatchScreenProps) {
  const isWin = winner === 1; // Assuming player is always 1
  const [showConfetti, setShowConfetti] = useState(false);
  const xpGained = calculateXP(matchStats, isWin);
  const currentLevel = 12; // Mock level
  const currentXP = 850; // Mock current XP
  const xpForNextLevel = 1000;
  const newXP = currentXP + xpGained;
  const xpProgress = newXP / xpForNextLevel * 100;
  useEffect(() => {
    if (isWin) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isWin]);
  const handleShare = async () => {
    const shareData = {
      title: 'Connectiply Match Result',
      text: `I ${isWin ? 'won' : 'played'} a Connectiply match in ${matchStats.moves} moves! 🎮`,
      url: 'https://connectiply.com'
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`I ${isWin ? 'won' : 'played'} a Connectiply match in ${matchStats.moves} moves! 🎮 https://connectiply.com`);
    }
  };
  return <div className="flex flex-col h-full p-6 bg-slate-50 items-center justify-center gap-8" data-id="element-395">
      {/* Confetti Effect */}
      <Confetti active={showConfetti} duration={4000} data-id="element-396" />

      {/* Result Header */}
      <div className="text-center space-y-2 animate-in zoom-in duration-300" data-id="element-397">
        <div className="text-6xl mb-4" data-id="element-398">{isWin ? '🎉' : '😢'}</div>
        <h1 className="text-4xl font-black text-slate-900" data-id="element-399">
          {isWin ? 'Congratulations!' : 'So Close!'}
        </h1>
        <p className="text-xl text-slate-500 font-medium" data-id="element-400">
          {isWin ? 'You crushed it!' : 'Better luck next time.'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="w-full max-w-sm space-y-3" data-id="element-401">
        <Card className="bg-white" data-id="element-402">
          <div className="grid grid-cols-3 gap-4 text-center" data-id="element-403">
            <div data-id="element-404">
              <div className="flex items-center justify-center mb-1" data-id="element-405">
                <Target className="text-blue-500" size={20} data-id="element-406" />
              </div>
              <div className="text-2xl font-black text-slate-900" data-id="element-407">
                {matchStats.moves}
              </div>
              <div className="text-xs text-slate-500" data-id="element-408">Moves</div>
            </div>
            <div data-id="element-409">
              <div className="flex items-center justify-center mb-1" data-id="element-410">
                <Zap className="text-amber-500" size={20} data-id="element-411" />
              </div>
              <div className="text-2xl font-black text-slate-900" data-id="element-412">
                {Math.floor(matchStats.duration / 60)}:
                {(matchStats.duration % 60).toString().padStart(2, '0')}
              </div>
              <div className="text-xs text-slate-500" data-id="element-413">Time</div>
            </div>
            <div data-id="element-414">
              <div className="flex items-center justify-center mb-1" data-id="element-415">
                <TrendingUp className="text-emerald-500" size={20} data-id="element-416" />
              </div>
              <div className="text-2xl font-black text-slate-900" data-id="element-417">
                {matchStats.connectedChips}
              </div>
              <div className="text-xs text-slate-500" data-id="element-418">Connected</div>
            </div>
          </div>
        </Card>

        {/* XP Card */}
        <Card className="bg-gradient-to-br from-blue-500 to-violet-600 text-white border-none shadow-xl" data-id="element-419">
          <div className="flex items-center justify-between mb-4" data-id="element-420">
            <div className="flex items-center gap-2" data-id="element-421">
              <div className="p-2 bg-white/20 rounded-lg" data-id="element-422">
                <Trophy size={20} data-id="element-423" />
              </div>
              <span className="font-bold" data-id="element-424">Level {currentLevel}</span>
            </div>
            <span className="font-bold text-blue-100" data-id="element-425">+{xpGained} XP</span>
          </div>
          <div className="space-y-2" data-id="element-426">
            <div className="h-3 bg-black/20 rounded-full overflow-hidden" data-id="element-427">
              <div className="h-full bg-white rounded-full transition-all duration-1000" style={{
              width: `${xpProgress}%`
            }} data-id="element-428" />
            </div>
            <div className="text-xs text-blue-100 text-right" data-id="element-429">
              {newXP} / {xpForNextLevel} XP
            </div>
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="w-full max-w-sm space-y-3" data-id="element-430">
        <Button fullWidth size="xl" onClick={onRematch} className="gap-2" data-id="element-431">
          <RotateCcw size={20} data-id="element-432" />
          Rematch
        </Button>

        <div className="grid grid-cols-2 gap-3" data-id="element-433">
          <Button variant="secondary" onClick={handleShare} className="gap-2" data-id="element-434">
            <Share2 size={18} data-id="element-435" />
            Share
          </Button>
          <Button variant="outline" onClick={onHome} className="gap-2" data-id="element-436">
            <Home size={18} data-id="element-437" />
            Home
          </Button>
        </div>
      </div>
    </div>;
}